import React from 'react';

function whenWillLandlordGetRent() {
  return (
    <>
      We’ll notify your landlord when you make your payment. Depending on the banks’ processing times, the rent will get
      deposited in their account up to 5 business days later.
    </>
  );
}

const faqs: {
  PAYMENT_SETTINGS_V2: {
    [key: string]: {
      title: string;
      body: () => Array<{ question: string; answer: string | JSX.Element; key?: string }>;
    };
  };
} = {
  PAYMENT_SETTINGS_V2: {
    FREQUENCY_PAGE: {
      title: 'You may be wondering...',
      body: () => [
        {
          question: 'What is the easiest way to set up my rent payments?',
          answer: `While it’s completely up to you, many renters find that
                    recurring payments are more convenient. You can set up your
                    payments once, then your rent will get paid automatically
                    every month.`,
        },
      ],
    },
    SCHEDULING_PAGE: {
      title: 'You may be wondering...',
      body: () => [
        {
          question: 'Will my rent arrive on time?',
          answer: `As long as you choose to send it on or before the payment date, we’ll
                    tell your landlord its paid on time. When they chose the payment date, we let them
                    know that payments take a few days to arrive.`,
        },
        {
          question: 'When will my landlord get my rent?',
          answer: whenWillLandlordGetRent(),
        },
      ],
    },
    CONFIRMATION_PAGE: {
      title: 'You may be wondering...',
      body: () => [
        {
          question: 'Where did the first and last send dates come from?',
          answer: `The First Send Date is the next available day from where
                    you choose to make your monthly payments. The Last Send Date is
                    based on an end date that your landlord set up.`,
        },
      ],
    },
    OVERDUE_SCHEDULING_PAGE: {
      title: 'You may be wondering...',
      body: () => [
        {
          question: 'Why can’t I schedule a payment for later?',
          answer: `Right now you have an overdue rent payment. Once it’s paid, you’ll be able
                    to schedule recurring payments or a future one-time payment.`,
        },
        {
          question: 'When will my landlord get my rent?',
          answer: whenWillLandlordGetRent(),
        },
      ],
    },
    PAYMENT_REPORTING: {
      title: 'FAQs',
      body: () => [
        {
          question: 'Why should I report my rent?',
          answer:
            'If you pay your rent on time every month, reporting your rent to credit bureaus can be ' +
            'an easy way to add positive payment behavior to your credit report. Rent reporting could ' +
            'be a good alternative to taking on loans or lines of credit that' +
            'might encourage additional spending.\n\n' +
            'Your credit score will depend on a number of factors that include, for example, your credit ' +
            'utilization ratio, the mix of credit you currently have open, and payment history. ' +
            'So, while paying rent and having it reported to credit bureaus ' +
            'can make a positive impact on your credit score, it will only ' +
            'ever be one of many factors that affect  your credit score.',
        },
        {
          question: 'How does rent reporting work?',
          answer: (
            <>
              Once you enroll in rent reporting through Zillow payments, the following will happen:
              <br />
              1. We’ll identify on-time payments made on our platform (within 30 days of the due date)
              <br />
              2. We’ll report those to Experian, Equifax, and TransUnion
              <br />
              3. A new tradeline will mark each on-time payment as a positive credit action
              <br />
              4. We’ll continue reporting your payments monthly as long as possible
              <br />
              <br />
              Payments will no longer be reported if you opt-out of the service, you fail to make a payment for three
              consecutive months, or if your landlord stops accepting Zillow payments. Zillow reserves the right to
              terminate this service at any time, see our{' '}
              <a
                target="_blank"
                href="https://www.zillow.com/z/rental/rent-reporting-terms-and-conditions/"
                rel="noreferrer"
              >
                Terms of Use.
              </a>
            </>
          ),
        },
        {
          question: 'What counts as an on-time payment?',
          answer:
            'Any payment processed on our platform within 30 days of the due date counts as ' +
            'an on-time payment. Should your payment be later than 30 days past due, we will not ' +
            'report that late payment to Experian, Equifax, or TransUnion. Our goal is to help you build ' +
            'credit, and you can opt-out of this service at any time.',
        },
        {
          question: 'What happens if a payment is late?',
          answer:
            'Late rent payments won’t be reported to Experian, Equifax, or TransUnion via Zillow Payments. ' +
            'However, if you want to improve your credit score, it’s important to consistently ' +
            'pay rent on time so those rent payments can be reported each month.',
        },
        {
          question: 'When will I see a change in my credit score?',
          answer:
            'Please allow at least 30 days for your credit score ' +
            '(FICO® 9/XD/10/10T and VantageScore® 3.0/4.0) to be updated ' +
            'once you begin reporting rent payments.',
        },
        {
          question: 'What information of mine is Zillow sharing with Experian, Equifax, and TransUnion?',
          answer: (
            <>
              To verify your identity and enable reporting of your on-time rent payments, the following data is shared
              monthly via secure/encrypted systems:
              <ul style={{ listStyle: 'disc', paddingLeft: '26px' }}>
                <li>Your identifying data (name, address, date of birth)</li>
                <li>Your lease data</li>
                <li>Your payment data</li>
              </ul>
            </>
          ),
        },
        {
          question: 'Can Zillow see my credit history?',
          answer: (
            <>
              No. We provide your payment data to Experian, Equifax, and TransUnion, but we do not do any soft or hard
              credit pulls on your credit report as part of this service. For instructions on how to view your credit
              report for free, consider using{' '}
              <a
                href="https://consumer.ftc.gov/articles/free-credit-reports#How%20To%20Get%20Your%20Free%20Credit%20Reports"
                target="_blank"
                rel="noreferrer"
              >
                this service
              </a>
              .
            </>
          ),
        },
        {
          question: 'Can I opt out of rent reporting?',
          answer: (
            <>
              Yes, you can opt out of this service at any time via your{' '}
              <a href="https://www.zillow.com/renter-hub/payments" target="_blank" rel="noreferrer">
                payments dashboard
              </a>
              . Please note that at this time we cannot support re-enrollment in this program once you have opted out
              during your current lease term.{' '}
            </>
          ),
        },
        {
          question: 'How much does rent reporting cost?',
          answer:
            'This service is free, when using Zillow payments. While many providers charge fees ' +
            'for rent payment reporting (which may include an initial enrollment fee and ' +
            'a monthly subscription fee), we believe renters should have the ' +
            'opportunity to build their credit at no cost.',
        },
        {
          question: 'I already have great credit. Do I need this?',
          answer:
            'Even renters with excellent credit may still see a ' +
            'boost from this service when they pay rent on time.',
        },
        {
          question: 'What happens if I see an inaccurate report on my credit report?',
          answer: (
            <>
              Renters have the ability to dispute any inaccurately reported payments. Keep in mind that it can take
              30-60 days for the bureaus to process data sent to them about your payment history. Disputes can be filed
              by contacting Zillow Customer Support, as part of the full dispute process which is outlined in our{' '}
              <a
                target="_blank"
                href="https://www.zillow.com/z/rental/rent-reporting-terms-and-conditions/"
                rel="noreferrer"
              >
                Terms of Use.
              </a>
            </>
          ),
        },
        {
          question: 'Which credit bureaus are my rent payments reported to?',
          answer: 'Zillow currently reports to Experian, Equifax, and TransUnion.',
        },
        {
          question: 'Which credit scores will this impact?',
          answer:
            'When enrolled in rent reporting, your on-time rent payments could ' +
            'increase your credit score with each on-time rental payment ' +
            '(assuming all of your other credit accounts are also in ' +
            'good standing). Specifically you’ll want to pay attention to credit ' +
            'scores that are designed to incorporate rental data such as ' +
            'VantageScore® 3.0 and 4.0 and FICO® Scores 9 and 10.\n\n' +
            'Keep in mind that your credit utilization ratio may be impacted by using a rent-reporting ' +
            'service, but these effects may be minimal, depending on your recent history.',
        },
        {
          question: 'I’m a landlord. How do I provide this service for my tenants?',
          answer: (
            <>
              This service is available to any renter who pays rent using Zillow Payments. Any landlord or property
              manager can provide access to this service by signing up for Zillow Rental Manager and setting up rent
              payments for their renter. Once a renter opts into the service, payments will begin being reported for
              that lease term. There’s no cost to renters or landlords for using our payments product.{' '}
              <a href="https://www.zillow.com/rental-manager/" target="_blank" rel="noreferrer">
                Get started here
              </a>
              .
            </>
          ),
        },
        {
          question: 'If I opt-out of rent payment reporting, can I opt in again in the future?',
          answer:
            'Once you opt out of Zillow rent payment reporting on a current lease, there is ' +
            'currently no way to opt into the program again. This includes renters who are automatically ' +
            'unenrolled due to insufficient payment history after 3 months of no on-time payment history.',
        },
      ],
    },
    CREDIT_TRACKING: {
      title: 'Credit Tracking FAQs',
      body: () => [
        {
          question: 'How much will my credit score go up from rent reporting?',
          answer: (
            <>
              Rent reporting can affect your credit score in different ways, depending on your current credit profile.
              Some people may see positive changes over time, especially those who are new to credit and consistently
              pay rent on time. In other cases, your score may go down a little bit when you first start reporting. This
              is like opening a new credit card or car loan account. Keep in mind that many other factors{' '}
              <a href="https://www.zillow.com/learn/calculate-credit-score/" target="_blank" rel="noreferrer">
                can affect your credit score
              </a>
              . For example, a missed credit card payment may influence your score more than rent reporting, potentially
              canceling out the positive impact of rent reporting.
            </>
          ),
          key: 'score_impact',
        },
        {
          question: 'Will this help me qualify for a mortgage in future?',
          answer:
            'All three major credit bureaus include rent payment information in credit reports if they receive it. FICO 9, FICO 10, and VantageScore consider rent payment information when calculating credit scores if it’s in your credit report, but some commonly used versions of FICO scores don’t. Depending on the score your mortgage lender uses, they may be able to use this data to help inform a mortgage decision.',
          key: 'mortgage_qualification',
        },
        {
          question: 'How long does it take to see changes in my credit score?',
          answer:
            'It typically takes 45 to 60 days for rent reporting data to appear on your credit report, and then it could take several months for the impact on your credit score to be noticeable. This is because the credit bureaus need time to process and update your information.',
          key: 'time_to_impact',
        },
        {
          question: 'Does rent reporting replace a traditional credit history?',
          answer:
            'No — rent reporting adds to your credit file, but it doesn’t replace credit cards, loans, or other types of credit used in scoring models.',
          key: 'credit_history',
        },
      ],
    },
  },
};

export default faqs;
